import React, { useState, useEffect } from 'react';
import { InAppMessageDto } from '../../types/InAppMessage';
import { inAppMessageApi } from '../../services/api';
import { format } from 'date-fns';

interface MessageListProps {
  onEditMessage: (message: InAppMessageDto) => void;
  onDeleteMessage: (id: string) => void;
  refreshTrigger: number;
}

const MessageList: React.FC<MessageListProps> = ({ onEditMessage, onDeleteMessage, refreshTrigger }) => {
  const [messages, setMessages] = useState<InAppMessageDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadMessages = async () => {
    try {
      setLoading(true);
      const data = await inAppMessageApi.getMessages();
      setMessages(data);
      setError(null);
    } catch (err) {
      setError('Failed to load messages');
      console.error('Error loading messages:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMessages();
  }, [refreshTrigger]);

  const handleDelete = async (id: string, title: string) => {
    if (window.confirm(`Are you sure you want to delete "${title}"?`)) {
      try {
        await inAppMessageApi.deleteMessage(id);
        onDeleteMessage(id);
        loadMessages(); // Refresh the list
      } catch (err) {
        alert('Failed to delete message');
        console.error('Error deleting message:', err);
      }
    }
  };

  const getPriorityBadge = (priority: number) => {
    const colors = {
      0: 'bg-gray-100 text-gray-800',
      1: 'bg-blue-100 text-blue-800',
      2: 'bg-yellow-100 text-yellow-800',
      3: 'bg-red-100 text-red-800'
    };
    const labels = { 0: 'Low', 1: 'Normal', 2: 'High', 3: 'Urgent' };
    
    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${colors[priority as keyof typeof colors] || colors[1]}`}>
        {labels[priority as keyof typeof labels] || 'Normal'}
      </span>
    );
  };

  const getMessageTypeBadge = (type: string) => {
    const colors: Record<string, string> = {
      info: 'bg-blue-100 text-blue-800',
      warning: 'bg-yellow-100 text-yellow-800',
      alert: 'bg-red-100 text-red-800',
      promotion: 'bg-green-100 text-green-800',
      weather: 'bg-purple-100 text-purple-800',
      app_update: 'bg-indigo-100 text-indigo-800',
      volunteer: 'bg-pink-100 text-pink-800'
    };
    
    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${colors[type] || colors.info}`}>
        {type}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="text-gray-500">Loading messages...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="text-red-800">{error}</div>
        <button 
          onClick={loadMessages} 
          className="mt-2 text-red-600 hover:text-red-800 underline"
        >
          Try again
        </button>
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="text-gray-500">No messages found</div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow overflow-hidden sm:rounded-md">
      <ul className="divide-y divide-gray-200">
        {messages.map((message) => (
          <li key={message.id} className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-3 mb-2">
                  <h3 className="text-lg font-medium text-gray-900 truncate">
                    {message.title}
                  </h3>
                  {getPriorityBadge(message.priority)}
                  {getMessageTypeBadge(message.messageType)}
                </div>
                <div className="flex items-center text-sm text-gray-500 space-x-4">
                  <span>Created: {format(new Date(message.createdAt), 'MMM d, yyyy HH:mm')}</span>
                  {message.expiresAt && (
                    <span>Expires: {format(new Date(message.expiresAt), 'MMM d, yyyy HH:mm')}</span>
                  )}
                </div>
                {(message.userId || message.deviceId) && (
                  <div className="flex items-center text-sm text-gray-500 space-x-4 mt-1">
                    {message.userId && <span>👤 User: {message.userId}</span>}
                    {message.deviceId && <span>📱 Device: {message.deviceId}</span>}
                  </div>
                )}
              </div>
              <div className="flex items-center space-x-2 ml-4">
                <button
                  onClick={() => onEditMessage(message)}
                  className="text-indigo-600 hover:text-indigo-900 text-sm font-medium"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(message.id, message.title)}
                  className="text-red-600 hover:text-red-900 text-sm font-medium"
                >
                  Delete
                </button>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default MessageList;